"use client";

import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { BaseAsset, EntityData } from '@akavirtual/deai-web-core';
import { VrmPreview } from './vrm-preview';

type Asset3DPreviewProps = {
    localFile?: File;
    assetId?: string;
    fileType?: string;
};

interface CreateSceneParams {
    assetId: string;
    file?: File;
    blobUrl?: string;
    assetType?: string;
}

// --- Scene Builder Function ---

/**
 * Dynamically constructs a scene definition for previewing a local asset file or remote asset.
 * @param {CreateSceneParams} params - The parameters needed to build the scene.
 * @returns {BaseAsset} A complete scene definition asset.
 */
const createPreviewScene = ({ assetId, file, blobUrl, assetType }: CreateSceneParams): BaseAsset => {
    // 1. Define common entities and components that are always needed.
    const mainCameraEntity: EntityData = {
        name: "MainCamera",
        transform: {
            position: [0, 1.3, -1.5],
            rotation: [0, 180, 0],
            scale: [1, 1, 1],
        },
        components: [{
            type: "Camera",
            properties: { fieldOfView: 60, nearClip: 0.1, farClip: 1000 },
        }],
        children:[]
    };

    // 2. Create the base for the model entity.
    const modelEntity: EntityData = {
        name: "PreviewModel",
        transform: {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        },
        components: [
            {
                type: 'Model',
                properties: {
                    asset: {
                        uuid: assetId,
                        ...(file && {
                            name: file.name,
                            type: assetType,
                            data: {
                                uri: blobUrl,
                            },
                        }),
                        // For remote assets, we only pass the uuid and the system will resolve it
                        ...(!file && assetType && {
                            type: assetType
                        })
                    },
                },
            },
        ],
        children:[]
    };

    // 3. Conditionally add components based on the file type.
    if (assetType === 'model/vrm+gltf') {
        // VRM files require Animator and Avatar components for full functionality.
        modelEntity.components.push({ type: 'Animator', properties: { controller: { uuid:'9b0c1d2e-3f4a-4c9d-a4e3-f2a1b0c9d8e7'} } });
        modelEntity.components.push({ type: 'Avatar', properties: {} });
    }

    // 4. Assemble the final scene asset.
    const sceneDefinition: BaseAsset = {
        uuid: uuidv4(),
        name: file ? "Local Asset Preview Scene" : "Remote Asset Preview Scene",
        type: "application/scene+json",
        data: {
            // A minimal environment can be defined here if needed
            environment: { 
                uuid: uuidv4(), 
                name: "PreviewEnvironment",
                type: 'model/gltf+json',
                data: {}
            },
            entities: [mainCameraEntity, modelEntity],
        },
    };

    return sceneDefinition;
};


// --- The Reusable React Component ---

export const Asset3DPreview = ({ localFile, assetId, fileType }: Asset3DPreviewProps) => {
    // Handle the case where we have an assetId instead of a local file
    const actualAssetId = useMemo(() => {
        if (assetId) return assetId; // Use the provided assetId directly
        return uuidv4(); // Generate a mock asset ID for local files
    }, [assetId]);

    // A temporary, browser-only URL for the local file.
    const blobUrl = useMemo(() => {
        if (!localFile) return undefined;
        // Clean up the previous blob URL if the file changes to prevent memory leaks.
        const url = URL.createObjectURL(localFile);
        return url;
    }, [localFile]);

    // Generate the scene definition using our builder function.
    // This logic re-runs only when the necessary inputs change.
    const previewSceneDefinition = useMemo(() => {
        // For local files
        if (localFile) {
            if (!blobUrl) return null;
            return createPreviewScene({
                assetId: actualAssetId,
                file: localFile,
                blobUrl,
                assetType: fileType,
            });
        }
        // For remote assets by ID
        else if (assetId) {
            return createPreviewScene({
                assetId,
                // assetType: fileType, @TODO: I don't know why assetType causes issues loading failure for remote assets, need to investigate further
            });
        }
        // Neither provided
        return null;
    }, [blobUrl, actualAssetId, localFile, assetId, fileType]);

    if (!previewSceneDefinition) {
        return <div className="h-full w-full flex items-center justify-center bg-muted">Loading...</div>;
    }

    return (
        <VrmPreview
            assetId={actualAssetId}
            sceneTemplate={previewSceneDefinition}
        />
    );
};